declare global {
  interface Window {
    LemonSqueezy?: {
      Setup?: (options?: Record<string, unknown>) => void
      Url?: {
        Open: (url: string) => void
      }
      /** LemonSqueezy affiliate tracking: the referring affiliate's id, if any. */
      Affiliate?: {
        GetID?: () => string | number | null | undefined
      }
    }
    createLemonSqueezy?: () => void
    __lemonSqueezyReady__?: Promise<void>
    __lemonSqueezyLoading__?: boolean
  }
}

const LEMON_SCRIPT_ID = 'lemonsqueezy-checkout-script'
const LEMON_SCRIPT_SRC = 'https://app.lemonsqueezy.com/js/lemon.js'

export default defineNuxtPlugin(() => {
  if (!import.meta.client) {
    return {
      provide: {
        lemonReady: () => Promise.resolve(),
        lemonLoading: () => false
      }
    }
  }

  /**
   * lemon.js is fetched the first time something asks for it (a checkout
   * button's useLemonSqueezy), not on every page load: on the homepage it sat
   * on the first-load network path for nobody, since the checkout only exists
   * once a CV has been generated or a plan is picked.
   */
  const ensureLemon = (): Promise<void> => {
    if (!window.__lemonSqueezyReady__) {
      window.__lemonSqueezyLoading__ = true
      window.__lemonSqueezyReady__ = new Promise<void>((resolve, reject) => {
        if (window.LemonSqueezy) {
          window.LemonSqueezy.Setup?.({
            eventHandler: (event: any) => {
              console.log('LemonSqueezy event:', event)
            }
          })
          window.__lemonSqueezyLoading__ = false
          resolve()
          return
        }

        const existingScript = document.getElementById(LEMON_SCRIPT_ID)
        if (existingScript) {
          existingScript.addEventListener('load', () => {
            window.createLemonSqueezy?.()
            window.LemonSqueezy?.Setup?.({
              eventHandler: (event: any) => {
                console.log('LemonSqueezy event:', event)
              }
            })
            window.__lemonSqueezyLoading__ = false
            resolve()
          }, { once: true })
          existingScript.addEventListener('error', () => {
            window.__lemonSqueezyLoading__ = false
            reject(new Error('Nepodařilo se načíst LemonSqueezy script'))
          }, { once: true })
          return
        }

        const script = document.createElement('script')
        script.id = LEMON_SCRIPT_ID
        script.src = LEMON_SCRIPT_SRC
        script.async = true
        script.dataset.cfasync = 'false'
        script.onload = () => {
          window.createLemonSqueezy?.()
          window.LemonSqueezy?.Setup?.({
            eventHandler: (event: any) => {
              console.log('LemonSqueezy event:', event)
            }
          })
          window.__lemonSqueezyLoading__ = false
          resolve()
        }
        script.onerror = () => {
          window.__lemonSqueezyLoading__ = false
          reject(new Error('Nepodařilo se načíst LemonSqueezy script'))
        }

        document.head.appendChild(script)
      })
    }
    return window.__lemonSqueezyReady__
  }

  return {
    provide: {
      lemonReady: ensureLemon,
      // Not requested yet counts as not loading, so nothing waits on a script
      // no one has asked for.
      lemonLoading: () => window.__lemonSqueezyLoading__ ?? false
    }
  }
})
